import { InputText } from "primereact/inputtext"
import { classNames } from "primereact/utils"
import { ConstantsResponseData, Country } from "../../api/types/constants"
import { useConstants } from "../../api/queries/useConstants"
import { ChangeEvent, useContext, useEffect, useState } from "react"
import { Dropdown, DropdownChangeParams } from "primereact/dropdown"
import FieldError, { FormErrors } from "../FieldError"
import commonStyles from "../common.module.css"
import { InputNumber, InputNumberChangeParams } from "primereact/inputnumber"
import { useQuery } from "react-query"
import { ShippingPriceAdditionalFee, ShippingPricePayload } from "../../api/types/shipping-price"
import { shippingPriceRequest } from "../../api/queries/shipping-price"
import { queryKeys } from "../../api/query"
import { AddressSimple } from "../../api/types/address"
import { Button } from "primereact/button"
import AddressBook from "../AddressBook"
import { ResponseDataError } from "../../api/types/base"
import { useDebounce } from "../../common/useDebounce"
import { useUserDetail } from "../../api/queries/useUserDetail"
import userContext from "../../context/user"
import { countryItemTemplate, countryValueTemplate } from "../common/country"
import { DataTable, DataTableSelectionChangeParams } from "primereact/datatable"
import { Column } from "primereact/column"

export const ShippingPriceForm = () => {
  const { user } = useContext(userContext)
  const { data: constants }: { data: ConstantsResponseData | undefined } = useConstants()
  const { data: userDetail } = useUserDetail(user?.uuid)

  const [errors, setErrors] = useState<FormErrors<ShippingPricePayload>>()

  const [loadingZip, setLoadingZip] = useState<string>("")
  const loadingZipDebounced = useDebounce(loadingZip, 500)
  const onLoadingZipChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLoadingZip(event.target.value)
    resetErrorsFor(["loadingZip"])
  }

  const [loadingCountry, setLoadingCountry] = useState<string>("")
  const onLoadingCountryChange = (event: DropdownChangeParams) => {
    setLoadingCountry(event.value)
    resetErrorsFor(["loadingCountry"])
  }

  const [unloadingZip, setUnloadingZip] = useState<string>("")
  const unloadingZipDebounced = useDebounce(unloadingZip, 500)
  const onUnloadingZipChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUnloadingZip(event.target.value)
    resetErrorsFor(["unloadingZip"])
  }

  const [unloadingCountry, setUnloadingCountry] = useState<string>("")
  const onUnloadingCountryChange = (event: DropdownChangeParams) => {
    setUnloadingCountry(event.value)
    resetErrorsFor(["unloadingCountry"])
  }

  const [totalWeight, setTotalWeight] = useState<number | null>(null)
  const totalWeightDebounced = useDebounce(totalWeight, 500)
  const onTotalWeightChange = (event: InputNumberChangeParams) => {
    setTotalWeight(event.value)
    resetErrorsFor(["totalWeight"])
  }

  const [totalVolume, setTotalVolume] = useState<number | null>(null)
  const totalVolumeDebounced = useDebounce(totalVolume, 500)
  const onTotalVolumeChange = (event: InputNumberChangeParams) => {
    setTotalVolume(event.value)
    resetErrorsFor(["totalVolume"])
  }

  const [selectedFees, setSelectedFees] = useState([])
  const onSelectedFeesChange = (e: DataTableSelectionChangeParams) => setSelectedFees(e.value)

  const [isLoadingAddressBookVisible, setIsLoadingAddressBookVisible] = useState<boolean>(false)
  const onLoadingAddressBookShow = () => setIsLoadingAddressBookVisible(true)
  const onLoadingAddressBookHide = () => setIsLoadingAddressBookVisible(false)
  const onLoadingAddressBookSelect = (address: AddressSimple) => {
    setLoadingCountry(address.country)
    setLoadingZip(address.zip)
    setIsLoadingAddressBookVisible(false)
    resetErrorsFor(["loadingCountry", "loadingZip"])
  }

  const [isUnloadingAddressBookVisible, setIsUnloadingAddressBookVisible] = useState<boolean>(false)
  const onUnloadingAddressBookShow = () => setIsUnloadingAddressBookVisible(true)
  const onUnloadingAddressBookHide = () => setIsUnloadingAddressBookVisible(false)
  const onUnloadingAddressBookSelect = (address: AddressSimple) => {
    setUnloadingCountry(address.country)
    setUnloadingZip(address.zip)
    setIsUnloadingAddressBookVisible(false)
    resetErrorsFor(["unloadingCountry", "unloadingZip"])
  }

  const resetErrorsFor = (names: string[]) => {
    let obj: { [key: string]: string } = {}

    names.forEach((name) => { obj[name] = "" })

    setErrors(Object.assign({}, errors, obj))
  }

  const payload: ShippingPricePayload = {
    loadingCountry,
    loadingZip: loadingZipDebounced,
    unloadingCountry,
    unloadingZip: unloadingZipDebounced,
    totalWeight: Number(totalWeightDebounced),
    totalVolume: totalVolumeDebounced,
    additionalFees: selectedFees.map((fee: ShippingPriceAdditionalFee) => fee.key),
  }

  const shippingPriceResponse = useQuery({
    queryFn: () => shippingPriceRequest(payload),
    queryKey: queryKeys.SHIPPING_PRICE(payload),
    enabled: Boolean(
      loadingCountry &&
      loadingZipDebounced &&
      unloadingCountry &&
      unloadingZipDebounced &&
      Number(totalWeightDebounced)
    ),
    onSuccess: () => {
      setErrors({})
    },
    onError: ({ response }) => {
      if (response.data?.errors) {
        let respErrors: FormErrors<ShippingPricePayload> = {}

        response.data.errors.forEach((error: ResponseDataError) => {
          if (typeof error.property === "string") {
            respErrors[error.property as keyof ShippingPricePayload] = error.message
          }
        })

        setErrors(respErrors)
      }
    }
  })

  const isValidCountry = (country: Country) => country.value === "CZ" || country.value === "SK"

  useEffect(() => {
    if (userDetail) {
      const address = userDetail?.results.userDetail.loadingDefaultAddress

      if (address && (address.country === "CZ" || address.country === "SK")) {
        setLoadingZip(address.zip)
        setLoadingCountry(address.country)
      }
    }
  }, [userDetail])

  return (
    <>
      <form className={"col-12"}>
        <div className={"p-fluid"}>
          <div className={"border-round-top flex block-title block-title_blue align-items-center px-3"}>
            Nakládka

            <Button type={"button"} label={"Adresář"} icon={"pi pi-book"} title={"Adresář"}
              className={"p-button-icon-only p-button-text ml-auto text-color"}
              onClick={onLoadingAddressBookShow} />
          </div>

          <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
            <div className={"grid"}>
              <div className={"field col-12 md:col-4"}>
                <span className="p-float-label">
                  <Dropdown options={(constants?.results.countries || []).filter(isValidCountry)}
                    value={loadingCountry} onChange={onLoadingCountryChange}
                    className={classNames({ "p-invalid": !!errors?.loadingCountry })}
                    itemTemplate={countryItemTemplate} valueTemplate={countryValueTemplate} />
                  <label className={commonStyles.labelRequired}>Stát:</label>
                </span>
                <FieldError error={errors?.loadingCountry} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={loadingZip} onChange={onLoadingZipChange}
                    className={classNames({ "p-invalid": !!errors?.loadingZip })}
                    aria-autocomplete={"none"} />
                  <label className={commonStyles.labelRequired}>PSČ:</label>
                </span>
                <FieldError error={errors?.loadingZip} />
              </div>
            </div>
          </div>

          <div className={"border-round-top flex block-title block-title_blue align-items-center px-3"}>
            Vykládka

            <Button type={"button"} label={"Adresář"} icon={"pi pi-book"} title={"Adresář"}
              className={"p-button-icon-only p-button-text ml-auto text-color"}
              onClick={onUnloadingAddressBookShow} />
          </div>

          <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
            <div className={"grid"}>
              <div className={"field col-12 md:col-4"}>
                <span className="p-float-label">
                  <Dropdown options={(constants?.results.countries || []).filter(isValidCountry)}
                    value={unloadingCountry} onChange={onUnloadingCountryChange}
                    className={classNames({ "p-invalid": !!errors?.unloadingCountry })}
                    itemTemplate={countryItemTemplate} valueTemplate={countryValueTemplate} />
                  <label className={commonStyles.labelRequired}>Stát:</label>
                </span>
                <FieldError error={errors?.unloadingCountry} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={unloadingZip} onChange={onUnloadingZipChange}
                    className={classNames({ "p-invalid": !!errors?.unloadingZip })}
                    aria-autocomplete={"none"} />
                  <label className={commonStyles.labelRequired}>PSČ:</label>
                </span>
                <FieldError error={errors?.unloadingZip} />
              </div>
            </div>
          </div>

          <div className={"border-round-top flex block-title block-title_blue align-items-center px-3"}>
            Zásilka
          </div>

          <div className={"border-solid border-1 border-300 px-3 pt-5"}>
            <div className={"grid"}>
              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputNumber value={totalWeight} onChange={onTotalWeightChange}
                    min={1} useGrouping={false}
                    className={classNames({ "p-invalid": !!errors?.totalWeight })} />
                  <label className={commonStyles.labelRequired}>Hmotnost (kg):</label>
                </span>
                <FieldError error={errors?.totalWeight} />
              </div>

              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputNumber value={totalVolume} onChange={onTotalVolumeChange}
                    min={0} step={0.1} minFractionDigits={3} maxFractionDigits={3} useGrouping={false}
                    className={classNames({ "p-invalid": !!errors?.totalVolume })} />
                  <label className={commonStyles.label}>Objem (cbm - m<sup>3</sup>):</label>
                </span>
                <FieldError error={errors?.totalVolume} />
              </div>
            </div>
          </div>
        </div>
      </form>

      <div className="col-12">
        <div className="grid">
          <div className="col-12 xl:col-6">
            <div className={"flex align-items-center mt-3"}>
              <label>Základní přepravné bez DPH:</label>
              <h2 className={"m-0 pl-3 white-space-nowrap"}>
                {shippingPriceResponse.isLoading
                  ? <span className="pi pi-spin pi-spinner" />
                  : shippingPriceResponse.data
                    ? `${shippingPriceResponse.data.results.price} Kč`
                    : <>&nbsp;</>
                }
              </h2>
            </div>
            <small className="text-red-500">Základní cena neobsahuje příplatkové služby a aktuální palivový příplatek.</small>

            <div className={"flex align-items-center mt-3"}>
              <label>Aktuální <strong>{shippingPriceResponse.data?.results.fuelFeePercent ? `${shippingPriceResponse.data?.results.fuelFeePercent}%` : ""}</strong> palivový příplatek:</label>
              <h2 className={"m-0 pl-3 white-space-nowrap"}>
                {shippingPriceResponse.isLoading
                  ? <span className="pi pi-spin pi-spinner" />
                  : shippingPriceResponse.data
                    ? `${shippingPriceResponse.data.results.fuelFeePrice} Kč`
                    : <>&nbsp;</>
                }
              </h2>
            </div>

            <div className={"flex align-items-center mt-3"}>
              <label>Celková cena bez DPH:</label>
              <h2 className={"m-0 pl-3 white-space-nowrap"}>
                {shippingPriceResponse.isLoading
                  ? <span className="pi pi-spin pi-spinner" />
                  : shippingPriceResponse.data
                    ? `${shippingPriceResponse.data.results.priceTotal} Kč`
                    : <>&nbsp;</>
                }
              </h2>
            </div>
          </div>

          <div className="col-12 xl:col-6">
            {shippingPriceResponse.data?.results.additionalFees.length && (
              <DataTable
                value={shippingPriceResponse.data.results.additionalFees}
                showGridlines
                size="small"
                className="mt-3"
                style={{
                  tableLayout: "fixed",
                  width: "100%"
                }}
                selectionMode={"checkbox"}
                selection={selectedFees}
                onSelectionChange={onSelectedFeesChange}
              >
                <Column
                  selectionMode="multiple"
                  headerStyle={{ width: "3rem", backgroundColor: "#cde9f7", }}
                />
                <Column
                  field="name"
                  header="Příplatky"
                  headerStyle={{ backgroundColor: "#cde9f7", }}
                />
                <Column
                  field="value"
                  header=""
                  headerStyle={{ backgroundColor: "#cde9f7", }}
                  body={(row) => `${row.value} Kč`}
                />
              </DataTable>
            )}
          </div>
        </div>
      </div>

      <AddressBook isVisible={isLoadingAddressBookVisible} onHide={onLoadingAddressBookHide}
        onSelect={onLoadingAddressBookSelect} countries={["CZ", "SK"]} />

      <AddressBook isVisible={isUnloadingAddressBookVisible} onHide={onUnloadingAddressBookHide}
        onSelect={onUnloadingAddressBookSelect} countries={["CZ", "SK"]} />
    </>
  )
}
